import * as React from "react";
import { useHistory } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import pluralize from "pluralize";
import { Button, Skeleton } from "@/components/ui";
import { HoloBorder, HoloDot, SocoAiLogo } from "@/components/Holo";
import { URLS } from "@/utils/routes";
import { useDynamicPromptsAccess } from "@/utils/useDynamicPromptsAccess";
import { PromptStudioHelpDialog } from "../help/PromptStudioHelpDialog";
import { useDesignPackages } from "../hooks/useDesignPackages";

function useCount(status: "published" | "draft" | "generating", enabled: boolean) {
  const query = useDesignPackages({ scope: "mine", status, perPage: 1 }, { enabled });
  return { ...query, count: query.data?.pagination.totalCount ?? 0 };
}

export function PromptStudioEntryCard(): JSX.Element | null {
  const history = useHistory();
  const { canView, canEdit } = useDynamicPromptsAccess();
  const [helpOpen, setHelpOpen] = React.useState(false);

  const published = useCount("published", canView);
  const drafts = useCount("draft", canView);
  const generating = useCount("generating", canView);

  if (!canView) return null;

  const isLoading = published.isLoading || drafts.isLoading || generating.isLoading;
  const isError = published.isError || drafts.isError || generating.isError;
  const hasDesigns = published.count + drafts.count + generating.count > 0;
  // A coach with no designs yet goes straight to New Design; anyone who can't create still just opens it.
  const startFresh = !isLoading && !isError && !hasDesigns && canEdit;

  const open = () => history.push(URLS.coach.promptStudio);
  const primary = () => history.push(startFresh ? URLS.coach.newDesign : URLS.coach.promptStudio);

  return (
    <>
      {/* The glow appears on hover, inviting the click. */}
      <HoloBorder radius="2xl" active={false} hoverActive>
        {/* The card click is a mouse shortcut; the button below is the keyboard and screen-reader path. */}
        <div
          className="flex flex-col gap-4 p-5 cursor-pointer sm:flex-row sm:items-center"
          data-testid="prompt-studio-entry-card"
          onClick={open}
        >
          <div className="flex flex-1 flex-col gap-1 min-w-0">
            <span className="flex items-center gap-1.5 text-lg font-bold text-slate-800">
              Prompt Studio, with
              <SocoAiLogo variant="color" height={14} />
            </span>
            <p className="text-base text-slate-500">
              Design a post once and approve it. Every player shares exactly what you approved, personalized for them.
            </p>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
              {isLoading ? (
                <Skeleton data-testid="entry-counts-skeleton" className="h-[14px] w-[120px]" />
              ) : (
                !isError &&
                hasDesigns && (
                  <>
                    <span data-testid="entry-counts">
                      {published.count} published · {drafts.count} {pluralize("draft", drafts.count)}
                    </span>
                    {generating.count > 0 && (
                      <span className="inline-flex items-center gap-1.5">
                        · <HoloDot /> {generating.count} generating
                      </span>
                    )}
                    <span aria-hidden="true">·</span>
                  </>
                )
              )}
              <Button
                variant="link"
                size="inline"
                className="px-0 text-xs text-slate-500"
                onClick={event => {
                  event.stopPropagation();
                  setHelpOpen(true);
                }}
              >
                How it works
              </Button>
            </div>
          </div>
          <Button
            variant="outline"
            size="lg"
            className="self-start border-primary text-primary hover:bg-primary/5 hover:text-primary sm:self-center"
            onClick={event => {
              event.stopPropagation();
              primary();
            }}
          >
            {startFresh ? "Create your first design" : "Open Prompt Studio"}
            <ArrowRight aria-hidden="true" />
          </Button>
        </div>
      </HoloBorder>
      <PromptStudioHelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
    </>
  );
}
